import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Check, X, Trash2 } from 'lucide-react';

// Components
import {
  DocumentsSidebar,
  DocumentsHeader,
  DocumentsTable
} from './components/Documents';

// Types
import { Document, Dataset } from './types';
import { useDispatch, useSelector } from 'react-redux';
import { deleteBulkDocument, getKnowledgeDetail } from 'slices/thunk';
import RetrievalTesting from './components/RetrievalTesting';

const Documents = () => {
  const { knowledgeId } = useParams<{ knowledgeId: string }>();
  const navigate = useNavigate();

  // State
  const [activeTab, setActiveTab] = useState<'documents' | 'retrieval' | 'settings'>('documents');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [dataset, setDataset] = useState<Dataset | null>(null);
  const [loading, setLoading] = useState(true);
  const [showMetadata, setShowMetadata] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isSelectAll, setIsSelectAll] = useState(false);

  const dispatch = useDispatch<any>();
  // const dataset = useSelector((state: any) => state.Knowledge.dataset);
  const knowledge = useSelector((state: any) => state.Knowledge.knowledgeDetail);

  useEffect(() => {
    if (knowledgeId) {
      setLoading(true);
      dispatch(getKnowledgeDetail(knowledgeId));
    }
  }, [knowledgeId]);

  useEffect(() => {
    if (knowledge) {
      setDataset(knowledge);
      setDocuments(knowledge.documents || []);
      setLoading(false);
    }
  }, [knowledge]);

  // Handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setIsSelectAll(true);
      setSelectedDocuments(documents.map(doc => doc.id));
    } else {
      setIsSelectAll(false);
      setSelectedDocuments([]);
    }
  };

  const handleSelectDocument = (documentId: string, checked: boolean) => {
    if (checked) {
      setSelectedDocuments([...selectedDocuments, documentId]);
    } else {
      setSelectedDocuments(selectedDocuments.filter(id => id !== documentId));
    }
  };

  const handleToggleDocument = (documentId: string) => {
    // Dispatch API to update document status

    setDocuments(docs =>
      docs.map(doc =>
        doc.id === documentId
          ? { ...doc, enabled: !doc.enabled }
          : doc
      )
    );
  };

  const filteredDocuments = documents.filter(doc =>
    doc.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(filteredDocuments.length / itemsPerPage);
  const paginatedDocuments = filteredDocuments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Navigation handlers
  const handleBackClick = () => {
    navigate('/knowledge');
  };

  const handleDocumentClick = (documentId: string) => {
    navigate(`/knowledge/${knowledgeId}/documents/${documentId}`);
  };

  const handleAddFile = () => {
    // TODO: Implement add file functionality
    navigate(`/knowledge/${knowledgeId}/document/create`);
    console.log('Add file clicked');
  };

  // Bulk action handlers
  const handleEnableSelected = () => {
    setDocuments(docs =>
      docs.map(doc =>
        selectedDocuments.includes(doc.id)
          ? { ...doc, enabled: true }
          : doc
      )
    );
    setSelectedDocuments([]);
  };

  const handleDisableSelected = () => {
    setDocuments(docs =>
      docs.map(doc =>
        selectedDocuments.includes(doc.id)
          ? { ...doc, enabled: false }
          : doc
      )
    );
    setSelectedDocuments([]);
  };

  const handleDeleteSelected = () => {
    // Dispatch API to delete selected documents
    dispatch(deleteBulkDocument({ id: knowledgeId!, documentIds: selectedDocuments }));
    setSelectedDocuments([]);
  };

  if (loading) {
    return (
      <div className="h-[calc(100vh-70px)] bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-sm text-gray-600">Loading documents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-70px)] bg-white flex flex-col lg:flex-row">
      {/* Sidebar - Hidden on mobile, shown on desktop */}
      <div className="hidden lg:block border-r border-gray-200">
        <DocumentsSidebar
          dataset={dataset}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          onBackClick={handleBackClick}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col bg-white min-h-0">
        {/* Header */}
        {activeTab === 'documents' && (
          <DocumentsHeader
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            showMetadata={showMetadata}
            onToggleMetadata={() => setShowMetadata(!showMetadata)}
            onAddFile={handleAddFile}
            onBackClick={handleBackClick}
          />)}

        {/* Mobile Navigation Tabs */}
        <div className="lg:hidden border-b border-gray-200 bg-white">
          <div className="flex space-x-1 px-4 py-2">
            <button
              onClick={() => setActiveTab('documents')}
              className={`flex-1 text-center py-2 px-3 text-sm font-medium rounded-md transition-colors ${activeTab === 'documents'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-700 hover:bg-gray-100'
                }`}
            >
              Documents
            </button>
            <button
              onClick={() => setActiveTab('retrieval')}
              className={`flex-1 text-center py-2 px-3 text-sm font-medium rounded-md transition-colors ${activeTab === 'retrieval'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-700 hover:bg-gray-100'
                }`}
            >
              Retrieval
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`flex-1 text-center py-2 px-3 text-sm font-medium rounded-md transition-colors ${activeTab === 'settings'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-700 hover:bg-gray-100'
                }`}
            >
              Settings
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-auto">
          {activeTab === 'documents' && (
            <div className="p-3 sm:p-4 lg:p-6 lg:pt-0">
              {/* Selection Actions Bar */}
              {selectedDocuments.length > 0 && (
                <div className="bg-blue-600 text-white rounded-lg p-3 mb-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:justify-between">
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                    <span className="font-medium text-sm sm:text-base">{selectedDocuments.length} Selected</span>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={handleEnableSelected}
                        className="flex items-center space-x-1 px-2 py-1 sm:px-3 sm:py-1 bg-white bg-opacity-20 rounded text-xs sm:text-sm hover:bg-opacity-30"
                      >
                        <Check className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span>Enable</span>
                      </button>
                      <button
                        onClick={handleDisableSelected}
                        className="flex items-center space-x-1 px-2 py-1 sm:px-3 sm:py-1 bg-white bg-opacity-20 rounded text-xs sm:text-sm hover:bg-opacity-30"
                      >
                        <X className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span>Disable</span>
                      </button>
                      <button
                        onClick={handleDeleteSelected}
                        className="flex items-center space-x-1 px-2 py-1 sm:px-3 sm:py-1 bg-red-500 rounded text-xs sm:text-sm hover:bg-red-600"
                      >
                        <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedDocuments([])}
                    className="text-white hover:text-gray-200 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              )}

              {/* Documents Table */}
              <DocumentsTable
                documents={paginatedDocuments}
                selectedDocuments={selectedDocuments}
                onSelectAll={handleSelectAll}
                onSelectDocument={handleSelectDocument}
                onDocumentClick={handleDocumentClick}
                onToggleDocument={handleToggleDocument}
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredDocuments.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={setItemsPerPage}
                isSelectAll={isSelectAll}
              />
            </div>
          )}

          {/* Retrieval Testing */}
          {activeTab === 'retrieval' && (
            <div className=" sm:p-4 h-full">
              <RetrievalTesting />
            </div>
          )}

          {/* Settings */}
          {activeTab === 'settings' && (
            <div className="p-3 sm:p-4 lg:p-6">
              <div className="bg-white rounded-lg border border-gray-200 p-6 sm:p-8 text-center">
                {/* <Settings className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-4" /> */}
                <h3 className="text-sm font-medium text-gray-900 mb-2">Knowledge Settings</h3>
                <p className="text-sm text-gray-600">Configure your knowledge base settings and preferences.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Documents;
